import * as Speech from 'expo-speech';
import { statsApi } from '../api';
import type { Landmark, RunSegment, AppSettings } from '../types';

function formatPace(minsPerMile: number): string {
  const mins = Math.floor(minsPerMile);
  const secs = Math.round((minsPerMile - mins) * 60);
  if (secs === 0) return `${mins} minutes per mile`;
  return `${mins} minute${mins !== 1 ? 's' : ''} and ${secs} second${secs !== 1 ? 's' : ''} per mile`;
}

function formatDuration(secs: number): string {
  const rounded = Math.round(secs);
  const m = Math.floor(rounded / 60);
  const s = rounded % 60;
  if (m === 0) return `${s} seconds`;
  if (s === 0) return `${m} minute${m !== 1 ? 's' : ''}`;
  return `${m} minute${m !== 1 ? 's' : ''} and ${s} second${s !== 1 ? 's' : ''}`;
}

function formatDelta(deltaSecs: number): string {
  const abs = Math.abs(deltaSecs);
  const direction = deltaSecs < 0 ? 'faster' : 'slower';
  return `${formatDuration(abs)} ${direction}`;
}

/** Announced when the runner reaches the very first landmark of a run. */
export function buildFirstLandmarkText(
  landmark: Landmark,
  totalDistMiles: number,
  totalDurSecs: number,
  settings: AppSettings
): string {
  const parts: string[] = [`You reached ${landmark.name}.`];
  if (settings.announcements.first_landmark) {
    if (settings.announcements.distance) {
      parts.push(`Total distance: ${totalDistMiles.toFixed(2)} miles.`);
    }
    if (settings.announcements.time) {
      parts.push(`Total time: ${formatDuration(totalDurSecs)}.`);
    }
    if (settings.announcements.pace && totalDistMiles > 0 && totalDurSecs > 0) {
      const pace = totalDurSecs / 60 / totalDistMiles;
      parts.push(`Average pace: ${formatPace(pace)}.`);
    }
  }
  return parts.join(' ');
}

export function announceFirstLandmark(
  landmark: Landmark,
  totalDistMiles: number,
  totalDurSecs: number,
  settings: AppSettings
): void {
  Speech.speak(buildFirstLandmarkText(landmark, totalDistMiles, totalDurSecs, settings), {
    language: 'en-US', rate: 0.5, pitch: 1.0,
  });
}

export async function announcePassedLandmark(landmark: Landmark): Promise<void> {
  Speech.speak(`You just passed ${landmark.name}`, { language: 'en-US', rate: 0.5, pitch: 1.0 });
}

export async function announceSegment(
  segment: RunSegment,
  settings: AppSettings,
  userId: string,
  ageGroup?: string,
  gender?: string,
  groupName?: string,
  speakFn: (text: string) => void = (t) => Speech.speak(t, { language: 'en-US', rate: 0.5, pitch: 1.0 })
): Promise<void> {
  const parts: string[] = [];
  const { fromLandmarkName, toLandmarkName, paceMinsPerMile, durationSecs, distanceMiles } = segment;
  const segmentKey = `${segment.fromLandmarkId}-${segment.toLandmarkId}`;

  parts.push(`You just ran from ${fromLandmarkName} to ${toLandmarkName}.`);

  if (settings.announcements.pace) {
    parts.push(`Your pace was ${formatPace(paceMinsPerMile)}.`);
  }
  if (settings.announcements.time) {
    parts.push(`Time: ${formatDuration(durationSecs)}.`);
  }
  if (settings.announcements.distance) {
    parts.push(`Distance: ${distanceMiles.toFixed(2)} miles.`);
  }

  // Personal stats
  const wantsPersonal =
    settings.announcements.vs_personal_last ||
    settings.announcements.vs_personal_best ||
    settings.announcements.vs_personal_avg;
  if (wantsPersonal) {
    try {
      const personal = await statsApi.personal(segmentKey);
      if (personal.count >= 1) {
        if (settings.announcements.vs_personal_last && personal.latestSecs > 0) {
          const delta = durationSecs - personal.latestSecs;
          if (delta === 0) {
            parts.push('Same time as your last run on this segment!');
          } else {
            parts.push(`Compared to your last time, that is ${formatDelta(delta)}.`);
          }
        }
      }
      if (personal.count > 1) {
        if (settings.announcements.vs_personal_best && personal.bestSecs > 0) {
          const delta = durationSecs - personal.bestSecs;
          if (delta === 0) {
            parts.push('That ties your personal best for this segment!');
          } else {
            parts.push(`Compared to your personal best, that is ${formatDelta(delta)}.`);
          }
        }
        if (settings.announcements.vs_personal_avg && personal.avgSecs > 0) {
          const delta = durationSecs - personal.avgSecs;
          const devs = Math.abs(personal.deviationsFromMean).toFixed(2);
          parts.push(
            `Compared to your average, that is ${formatDelta(delta)}, or ${devs} standard deviations ${delta < 0 ? 'faster' : 'slower'} than your mean.`
          );
        }
      }
    } catch {
      // stats unavailable — skip
    }
  }

  // Community stats
  const communityChecks: Array<['age' | 'gender' | 'named', string, boolean]> = [
    ['age', ageGroup ?? '', settings.announcements.vs_group_age],
    ['gender', gender ?? '', settings.announcements.vs_group_gender],
    ['named', groupName ?? '', settings.announcements.vs_group_named],
  ];
  for (const [groupType, groupValue, enabled] of communityChecks) {
    if (!enabled || !groupValue) continue;
    try {
      const community = await statsApi.community(segmentKey, groupType, groupValue);
      if (community.participantCount >= 5) {
        const delta = durationSecs - community.avgSecs;
        const devs = Math.abs(
          (durationSecs - community.avgSecs) / (community.stdDevSecs || 1)
        ).toFixed(2);
        const groupLabel = groupType === 'named' ? groupValue : `your ${groupType} group`;
        parts.push(
          `Compared to ${groupLabel}, that is ${formatDelta(delta)}, or ${devs} standard deviations ${delta < 0 ? 'faster' : 'slower'}.`
        );
      }
    } catch {
      // community stats unavailable — skip
    }
  }

  speakFn(parts.join(' '));
}
